import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { colors, fontFamilies } from '../baseStyle/Variables';
import FirstProgress from './first-progress';
import SecondProgress from './second-progress';
import ThirdProgress from './third-progress';
import { useTranslation } from 'react-i18next';
import { FormTypes } from '../../gql/graphql';
import { AppStatuses } from '../../gql/graphql';

const CHECK_ELIGIBILITY: string[] = [
  'BasicInfo',
  'BasicsContinued',
  'AccommodationAddress',
  'AccommodationAddressErrors',
  'AccommodationAddressLoading',
  'NoAccountFound',
];

const PAYMENT_DETAILS: string[] = [
  'LeaseApplication.BankDetails',
  'LeaseApplication.DebitCardEntry',
  'LeaseApplication.DebitCardError',
  'LeaseApplication.PayFrequency',
  'LeaseApplication.PaymentSelection.PaymentSelectionInit',
  'LeaseApplication.PaymentSelection.FetchLinkToken',
  'LeaseApplication.PaymentReview',
  'LeaseApplication.ShippingAddress',
  'VirtualCardApplication.BankDetails',
  'VirtualCardApplication.DebitCardEntry',
  'VirtualCardApplication.DebitCardError',
  'VirtualCardApplication.PayFrequency',
  'DebitCardHardDecline',
  'DebitCardSoftDecline',
];

interface ProgressBarProps {
  currentNode: [string] | undefined;
  formType: FormTypes;
  highestSupportedApplicationMilestone: number;
  appStatus?: AppStatuses;
}

const ProgressBarContainer = styled.div`
  font-family: ${fontFamilies.interLight};
  display: flex;
  justify-content: space-evenly;
  margin-top: 0.5rem;
`;

const HeaderBottom = styled.div`
  display: flex;
  align-items: center;
  height: 14px;
  width: 100%;
`;
const GreyLine = styled.div`
  height: 1px;
  background-color: ${colors.lightGrey};
  width: 100%;
`;

type HighlightedPB = {
  eligibility: boolean;
  details: boolean;
  review: boolean;
};

const ProgressBar: React.FC<ProgressBarProps> = ({ currentNode, formType, highestSupportedApplicationMilestone, appStatus }: ProgressBarProps) => {
  const { t } = useTranslation();
  const [highlightedSections, setHighlightedSections] = useState<HighlightedPB>({ eligibility: false, details: false, review: false });

  const deepCheckCurrentNode = JSON.stringify(currentNode);

  useEffect(() => {
    determineHighlightedSection();
  }, [deepCheckCurrentNode]); // eslint-disable-line react-hooks/exhaustive-deps

  const determineHighlightedSection = () => {
    if (!!currentNode?.length) {
      const isM4 = highestSupportedApplicationMilestone > 3;
      const isReadOnly = appStatus
        ? [AppStatuses.DocsSigned, AppStatuses.ApprovedForFunding, AppStatuses.Funded, AppStatuses.ReadyForFunding].includes(appStatus)
        : false;
      const longFormProgressBarEligibilityNodes = isM4 ? ['personalInformation', 'contactInformation', 'accommodationAddress'] : [];
      const longFormProgressBarDetailsNodes = isM4
        ? ['bankingInformation', 'debitCardInformation', 'DebitCardEntryInit', 'UpdateDebitCard', 'incomeSource']
        : [];
      const longFormProgressBarReviewNodes = isM4
        ? ['reviewAndSubmit', 'ContractSummaryBM', 'ContractSummaryEcomm', ...(isReadOnly ? [] : ['LeaseAgreement'])]
        : [];
      for (let i = 0; i < currentNode.length; i++) {
        if ([...CHECK_ELIGIBILITY, ...longFormProgressBarEligibilityNodes].includes(currentNode[i])) {
          setHighlightedSections({
            eligibility: true,
            details: false,
            review: false,
          });
          return;
        } else if ([...PAYMENT_DETAILS, ...longFormProgressBarDetailsNodes].includes(currentNode[i])) {
          setHighlightedSections({
            eligibility: false,
            details: true,
            review: false,
          });
          return;
        } else if (longFormProgressBarReviewNodes.includes(currentNode[i])) {
          setHighlightedSections({
            eligibility: false,
            details: false,
            review: true,
          });
          return;
        } else {
          setHighlightedSections({
            eligibility: false,
            details: false,
            review: false,
          });
        }
      }
    } else {
      setHighlightedSections({
        eligibility: false,
        details: false,
        review: false,
      });
    }
  };

  const shouldShowProgressBar = () => {
    const showOnSection = highlightedSections.eligibility || highlightedSections.details || highlightedSections.review;
    return showOnSection && !!currentNode;
  };

  return shouldShowProgressBar() ? (
    <>
      <ProgressBarContainer role="region" aria-label={t(($) => $.componentProgressBar.progressBarTitle)}>
        <FirstProgress
          progressText={t(($) => (formType === FormTypes.Short ? $.componentProgressBar.checkEligibility : $.componentProgressBar.personalInfo))}
          isHighlighted={highlightedSections}
        ></FirstProgress>
        <SecondProgress
          progressText={t(($) => (formType === FormTypes.Short ? $.componentProgressBar.additionalDetails : $.componentProgressBar.bankingInfo))}
          isHighlighted={highlightedSections}
        ></SecondProgress>
        <ThirdProgress progressText={t(($) => $.componentProgressBar.reviewAndCheckout)} isHighlighted={highlightedSections}></ThirdProgress>
      </ProgressBarContainer>
    </>
  ) : (
    <HeaderBottom>
      <GreyLine />
    </HeaderBottom>
  );
};

export default ProgressBar;
